import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, QueryExecutionStatisticsDTO } from '../../../../services/api/api';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from '../../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../../services/global/RequestClass';
import { FilterPanelConfig } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-query-performance',
  templateUrl: './query-performance.component.html',
  styleUrls: ['./query-performance.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueryPerformanceComponent extends RequestClass {
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  private readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  queries: QueryExecutionStatisticsDTO[] = [];
  displayedQueries: QueryExecutionStatisticsDTO[] = [];

  // filter variables
  searchStr: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: false,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    const sub = this.api
      ._admin_AdminEndpoints_queryExecutionStatistics()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (queryData: QueryExecutionStatisticsDTO[]) => {
          this.queries = queryData;
          this.search();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  sort() {
    let sorted = Object.assign([], this.displayedQueries);
    if (this.displayedQueries.length > 0) {
      sorted = sorted.sort((a, b) =>
        (
          a.resourceDescriptor.packageName +
          '.' +
          a.resourceDescriptor.className +
          '.' +
          a.resourceDescriptor.methodName +
          ':' +
          a.resourceDescriptor.lineNumber
        ).localeCompare(
          b.resourceDescriptor.packageName +
            '.' +
            b.resourceDescriptor.className +
            '.' +
            b.resourceDescriptor.methodName +
            ':' +
            b.resourceDescriptor.lineNumber
        )
      );
    }
    this.displayedQueries = sorted;
  }

  search() {
    if (this.searchStr) {
      this.displayedQueries = [];
      for (const query of this.queries) {
        if (
          (
            query.resourceDescriptor.packageName.toLowerCase() +
            '.' +
            query.resourceDescriptor.className.toLowerCase() +
            '.' +
            query.resourceDescriptor.methodName.toLowerCase() +
            ':' +
            query.resourceDescriptor.lineNumber.toString()
          ).includes(this.searchStr.toLowerCase())
        ) {
          this.displayedQueries.push(query);
        }
      }
    } else {
      this.displayedQueries = this.queries;
    }
    this.sort();
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.search();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.searchStr = filterMap.get('search')?.[0];
  }
}
